import { createCaptchaWidget } from "@modules/captcha/captchaWidget.ts"
import { submitReport } from "@modules/form/report-star-post/report-star-post-api.ts"
import { addEventListenerAll } from "@modules/util/events.ts"

let submitReportButton: HTMLElement | null = null
let reportModal: HTMLElement | null = null
let form: HTMLFormElement | null = null
let modalInputSection: HTMLElement | null = null
let modalStatusSection: HTMLElement | null = null
let captchaContainer: HTMLElement | null
let captchaFieldsController: AbortController | null = null
let datenschutzCheckbox: HTMLInputElement | null
let reportTextarea: HTMLTextAreaElement | null
let reportedContentPreview: HTMLElement | null = null

let messageInputFieldWritten = false
let datenschutzAccepted = false
let captchaIsDone = false
let dsgvoInputFieldWritten = false
let hasTextInReportMessageField = false

export function reportStarPostEntry() {
    reportModal = document.querySelector<HTMLElement>("#report-post-modal") as HTMLElement
    form = reportModal?.querySelector<HTMLFormElement>("#versand-form-1") as HTMLFormElement
    submitReportButton = reportModal?.querySelector<HTMLElement>(
        "#report-post-modal [type='submit']",
    ) as HTMLTextAreaElement
    captchaContainer = reportModal?.querySelector<HTMLElement>(".friendly-captcha") ?? null
    modalInputSection = reportModal?.querySelector<HTMLElement>(".modal-form") as HTMLElement
    modalStatusSection = reportModal?.querySelector<HTMLElement>(".modal-status") as HTMLElement
    datenschutzCheckbox = form?.querySelector<HTMLInputElement>('input[name="acceptDatenschutz"]') ?? null
    reportedContentPreview = reportModal?.querySelector<HTMLElement>(".stars-posts-report-modal-header") as HTMLElement

    reportTextarea = document.querySelector('[name="reportReason"]') as HTMLTextAreaElement

    form?.reset()
    checkDisabledSubmitButton()
    showReportModalPreview()
    hideStatusSection()
    showInputSection()
    initCaptcha()
    initFieldValidation()
    initFieldInput()
    initReportFormular()
}

function initCaptcha(): void {
    if (!captchaContainer) {
        return
    }
    let captchaAlreadyStarted = false

    // Alte Listener abbrechen, falls initCaptcha erneut aufgerufen wird
    captchaFieldsController?.abort()
    captchaFieldsController = new AbortController()

    const widget = createCaptchaWidget(captchaContainer, () => {
        captchaIsDone = true
        updateSubmitButton()
    })

    const formFields = form?.querySelectorAll<HTMLElement>("textarea, input") ?? []

    addEventListenerAll(
        Array.from(formFields),
        "click",
        () => {
            if (captchaAlreadyStarted) {
                return
            }
            widget.start()
            captchaAlreadyStarted = true
        },
        { signal: captchaFieldsController.signal },
    )
}

function initFieldValidation(): void {
    reportTextarea?.addEventListener("input", (event) => {
        const value = (event.target as HTMLTextAreaElement).value ?? ""
        hasTextInReportMessageField = value.trim().length > 0
        updateSubmitButton()
    })

    datenschutzCheckbox?.addEventListener("input", (event) => {
        datenschutzAccepted = (event.target as HTMLInputElement).checked
        updateSubmitButton()
    })
}

function updateSubmitButton(): void {
    if (!submitReportButton) {
        return
    }

    const frcSolutionValue =
        form?.querySelector<HTMLInputElement>('input[name="frc-captcha-solution"]')?.value ?? ".UNSTARTED"

    const allValid =
        datenschutzAccepted && captchaIsDone && hasTextInReportMessageField && frcSolutionValue !== ".UNSTARTED"

    if (allValid) {
        submitReportButton.removeAttribute("disabled")
    } else {
        submitReportButton.setAttribute("disabled", "disabled")
    }
}

async function handleSubmit(event: Event) {
    event.preventDefault()

    const postId = reportModal?.querySelector<HTMLInputElement>(".report-post-id")?.value
    if (!postId) {
        throw new Error("Post ID für die Meldung konnte nicht gefunden werden.")
    }
    const creatorId = reportModal?.querySelector<HTMLInputElement>(".report-creator-id")?.value
    if (!creatorId) {
        throw new Error("Creator ID für die Meldung konnte nicht gefunden werden.")
    }

    try {
        const reportMessage = reportTextarea?.value || ""
        if (reportMessage === "") {
            return
        }
        const result = await submitReport(postId, reportMessage, creatorId)

        if (result.status === 202) {
            hideInputSection()
            hideReportModalPreview()
            renderStatusMessage()
            showStatusSection()

            form?.reset()
            return
        }

        hideInputSection()
        hideReportModalPreview()
        showStatusSection()

        form?.reset()
        return
    } catch (error) {
        showInputSection()
        hideStatusSection()
        renderErrorStatusMessage(
            "Beim Versenden der Meldung ist ein Fehler aufgetreten. Bitte versuche es später erneut.",
        )
        form?.reset()

        return
    }
}

function checkDisabledSubmitButton() {
    const frcSolutionValue =
        document.querySelector<HTMLInputElement>('#report-modal input[name="frc-captcha-solution"]')?.value ||
        ".UNSTARTED"
    submitReportButton?.setAttribute("disabled", "true")

    if (!messageInputFieldWritten || !dsgvoInputFieldWritten || !captchaIsDone || frcSolutionValue === ".UNSTARTED") {
        submitReportButton?.setAttribute("disabled", "true")
    } else {
        submitReportButton?.removeAttribute("disabled")
    }
}

function hideReportModalPreview() {
    if (reportedContentPreview) {
        reportedContentPreview.classList.add("hidden")
    }
}

function showReportModalPreview() {
    if (reportedContentPreview) {
        reportedContentPreview.classList.remove("hidden")
    }
}

function initFieldInput() {
    document.querySelector<HTMLElement>("#custom-ldsfehlermelden-checkbox-3")?.addEventListener("input", (event) => {
        dsgvoInputFieldWritten = (event.target as HTMLInputElement)?.checked || false

        checkDisabledSubmitButton()
    })

    document
        .querySelector<HTMLTextAreaElement>("#report-modal textarea[name='nachricht']")
        ?.addEventListener("input", (event) => {
            const target = event.target as HTMLInputElement
            const feedbackMessage = target.value || ""

            messageInputFieldWritten = false

            if (feedbackMessage !== "") {
                messageInputFieldWritten = true
            }

            checkDisabledSubmitButton()
        })
}

function initReportFormular() {
    if (!form) {
        return
    }

    form?.addEventListener("submit", handleSubmit, { once: true })
}

function hideInputSection() {
    if (modalInputSection) {
        modalInputSection.classList.add("hidden")
    }
}

function showStatusSection() {
    if (modalStatusSection) {
        modalStatusSection.classList.remove("hidden")
        modalStatusSection.style.display = "block"
    }
}

function hideStatusSection() {
    if (modalStatusSection) {
        modalStatusSection.classList.add("hidden")
    }
}

function renderStatusMessage(): void {
    reportModal?.classList.add("success")

    const statusBody = modalStatusSection?.querySelector<HTMLElement>(".modal-body")
    if (!modalStatusSection || !statusBody) {
        return
    }

    const modalBody = modalStatusSection?.querySelector<HTMLElement>(".modal-body.text-center")
    if (!modalBody) {
        return
    }

    modalBody.style.display = "flex"
    modalBody.style.flexDirection = "column"

    let html = ""
    if (SiteInfo.isMobileVersion) {
        html = `

                        <span class="icon light">
                        <svg class="icon-confirm-success" data-icon-id="check-mark-thin" viewBox="0 0 20 20"><path d="m2.289 9.386 5.842 5.004 9.558-9.364.303.308-9.841 9.642-.15-.129-5.992-5.132z"></path></svg>
                    </span>
                    <strong>Beitrag wurde gemeldet!</strong>
                  </p>
                  <p>Wir werden den Beitrag baldmöglichst überprüfen.</p>`
    } else {
        html = `<span class="icon light">
                            <svg class="icon-confirm-success" data-icon-id="check-mark-thin" viewBox="0 0 20 20"><path d="m2.289 9.386 5.842 5.004 9.558-9.364.303.308-9.841 9.642-.15-.129-5.992-5.132z"></path></svg>
                        </span>
                        <p class="success-title column-heading">
                            <strong>Beitrag wurde gemeldet!</strong>
                        </p>
                        <p>Wir werden den Beitrag baldmöglichst überprüfen.</p>`
    }

    statusBody.innerHTML = html
}

function renderErrorStatusMessage(message: string): void {
    const statusBody = modalStatusSection?.querySelector<HTMLElement>(".modal-body")
    if (!modalStatusSection || !statusBody) {
        return
    }

    let html = ""
    if (SiteInfo.isMobileVersion) {
        html = `<div class="modal-body text-center">
            <p class="success-title column-heading">
                <strong>${message}</strong>
            </p>
        </div>`
    } else {
        html = `<div class="modal-body text-center">
            <p class="success-title column-heading">
                <strong>${message}</strong>
            </p>
        </div>`
    }

    statusBody.innerHTML = html
}

function showInputSection() {
    if (modalInputSection) {
        modalInputSection.classList.remove("hidden")
        modalInputSection.style.display = "block"
    }
}
